import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Appointment, AppointmentCheck, AppointmentInput, AppointmentStatus, AppointmentUpdate, AppointmentWarning, AvailabilitySlot, Location, LocationInput } from "@plataforma/shared";
import { api } from "../../lib/api";

export const useLocations = () => useQuery({ queryKey: ["locations"], queryFn: async () => (await api.get<{ locations: Location[] }>("/locations")).locations });

export const useAppointments = (from: string, to: string) =>
  useQuery({ queryKey: ["appointments", "range", from, to], queryFn: async () => (await api.get<{ appointments: Appointment[] }>(`/appointments?from=${from}&to=${to}`)).appointments, placeholderData: (previous) => previous });

export const checkAppointment = async (input: AppointmentCheck) => (await api.post<{ warnings: AppointmentWarning[] }>("/appointments/check", input)).warnings;

function useRefresh() {
  const queryClient = useQueryClient();
  return () => Promise.all(["appointments", "invoices", "clients", "locations", "finance"].map((key) => queryClient.invalidateQueries({ queryKey: [key] })));
}

type Saved = { appointment: Appointment; warnings: AppointmentWarning[] };

export function useSaveAppointment(id?: string) {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (input: AppointmentInput | AppointmentUpdate) => (id ? api.put<Saved>(`/appointments/${id}`, input) : api.post<Saved>("/appointments", input)),
    onSuccess: refresh,
  });
}

export function useChangeStatus() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: AppointmentStatus }) => (await api.post<{ appointment: Appointment }>(`/appointments/${id}/status`, { status })).appointment,
    onSuccess: refresh,
  });
}

function useLocationMutation<Input>(run: (input: Input) => Promise<{ locations: Location[] }>) {
  const queryClient = useQueryClient();
  return useMutation({ mutationFn: async (input: Input) => (await run(input)).locations, onSuccess: (locations) => queryClient.setQueryData(["locations"], locations) });
}
export const useCreateLocation = () => useLocationMutation((input: LocationInput) => api.post("/locations", input));
export const useRemoveLocation = () => useLocationMutation((id: string) => api.del(`/locations/${id}`));
export const useSetAvailability = () => useLocationMutation(({ id, slots }: { id: string; slots: AvailabilitySlot[] }) => api.put(`/locations/${id}/availability`, { slots }));
